import { z } from 'zod';

// Define different preference categories
export enum PreferenceCategory {
  UI = 'ui',
  CHECKOUT = 'checkout',
  INVENTORY = 'inventory',
  RECEIPTS = 'receipts',
  GENERAL = 'general',
  RESTAURANT_POS = 'restaurantPos',
}

// UI Preferences Schema
export const UIPreferencesSchema = z.object({
  theme: z.enum(['light', 'dark', 'auto']).default('light'),
  language: z.string().default('en'),
  currencyFormat: z.string().default('USD'),
  dateFormat: z.string().default('MM/DD/YYYY'),
  timeFormat: z.enum(['12h', '24h']).default('12h'),
  compactMode: z.boolean().default(false),
  showCategoryFilterExpanded: z.boolean().default(true),
  // 'quick-pay' kept only as a tolerated legacy value so existing persisted
  // preferences still parse; the original quick-pay page is retired and nothing
  // routes to it anymore. New/missing values default to the app hub.
  startupPage: z.enum(['app-hub', 'quick-pay']).default('app-hub'),
});

// Quick Pay Usage Item Schema
export const QuickPayUsageItemSchema = z.object({
  cents: z.number().min(0),
  usageCount: z.number().min(1).default(1),
  lastUsed: z.date().default(() => new Date()),
  firstUsed: z.date().default(() => new Date()),
});

// Quick Pay Usage Tracking Schema
export const QuickPayUsageTrackingSchema = z
  .object({
    amounts: z.array(QuickPayUsageItemSchema).default([]),
    maxStoredAmounts: z.number().min(5).max(50).default(20),
    displayCount: z.number().min(1).max(10).default(4),
  })
  .default({});

// Checkout Preferences Schema
export const CheckoutPreferencesSchema = z.object({
  defaultView: z.enum(['products', 'custom']).default('custom'),
  autoFocusBarcode: z.boolean().default(true),
  showTaxSeparately: z.boolean().default(true),
  confirmBeforeRemoveItem: z.boolean().default(false),
  enableQuickPay: z.boolean().default(true),
  defaultPaymentMethod: z.string().default('cash'),
  autoOpenCashDrawer: z.boolean().default(true),
  quickPayUsageTracking: QuickPayUsageTrackingSchema,
  lastServiceType: z.enum(['to-go', 'dine-in']).default('to-go'),
  enableSaleSounds: z.boolean().default(true),
  enableScanSounds: z.boolean().default(true),
  makeItemChangesPermanent: z.boolean().default(true),
});

// Inventory Preferences Schema
export const InventoryPreferencesSchema = z.object({
  defaultView: z.enum(['grid', 'list']).default('list'),
  itemsPerPage: z.number().min(10).max(100).default(25),
  showOutOfStock: z.boolean().default(true),
  autoGenerateBarcode: z.boolean().default(false),
  defaultProductType: z.string().default('GENERAL'),
  trackStockByDefault: z.boolean().default(true),
  lowStockThreshold: z.number().min(0).default(5),
});

// Receipt Preferences Schema
export const ReceiptPreferencesSchema = z.object({
  autoPrint: z.boolean().default(true),
  printCustomerCopy: z.boolean().default(false),
  includeBusinessLogo: z.boolean().default(true),
  showTaxBreakdown: z.boolean().default(true),
  receiptFormat: z.enum(['standard', 'compact', 'detailed']).default('standard'),
  lastUsedReceiptTab: z
    .number()
    .int()
    .min(0)
    .max(3)
    .transform((value) => Math.min(value, 2))
    .default(0), // 0=Print, 1=Email, 2=Text
  // Register-local kitchen ticket size. Migrate the original "normal" size to "small".
  orderReceiptSize: z
    .enum(['small', 'medium', 'large', 'normal'])
    .default('small')
    .transform((value) => (value === 'normal' ? 'small' : value)),
});

// General Preferences Schema
export const GeneralPreferencesSchema = z.object({
  businessName: z.string().default(''),
  businessAddress: z.string().default(''),
  businessPhone: z.string().default(''),
  businessEmail: z.string().default(''),
  taxRate: z.number().min(0).max(1).default(0.0875), // Default 8.75%
  enableNotifications: z.boolean().default(true),
  autoBackupFrequency: z.enum(['daily', 'weekly', 'monthly', 'never']).default('weekly'),
});

// Restaurant POS display preferences are register-local. Business-wide
// behavior such as held orders and the default opening tab remains in the
// Access V2 business policy; the `*Visible` switches can only hide a feature
// the policy allows on this register, never enable one it turned off.
//
// Newer keys also `.catch()` back to their default: a single malformed value
// would otherwise fail the whole document and reset every category.
export const RestaurantPosPreferencesSchema = z.object({
  productView: z.enum(['image', 'compact']).default('compact'),
  gridDensity: z.enum(['compact', 'cozy', 'large']).default('cozy'),
  textSize: z.enum(['small', 'medium', 'large']).default('medium'),
  showPrices: z.boolean().default(true),
  showQuickAmounts: z.boolean().default(true),
  ticketDensity: z.enum(['compact', 'cozy', 'large']).default('cozy'),
  favoriteTileStyle: z.enum(['color', 'image', 'text']).default('color'),
  theme: z.enum(['light', 'dark']).default('light'),
  searchButtonVisible: z.boolean().default(true).catch(true),
  holdFeatureVisible: z.boolean().default(true).catch(true),
  staffMealsVisible: z.boolean().default(true).catch(true),
  orderAlertSoundEnabled: z.boolean().default(true).catch(true),
});

// Combined Preferences Schema
export const PreferencesSchema = z.object({
  ui: UIPreferencesSchema,
  checkout: CheckoutPreferencesSchema,
  inventory: InventoryPreferencesSchema,
  receipts: ReceiptPreferencesSchema,
  general: GeneralPreferencesSchema,
  // Records written before Restaurant POS display preferences existed do not
  // contain this category. Default it at the schema boundary so those records
  // remain valid while consumers still receive a fully populated object.
  restaurantPos: RestaurantPosPreferencesSchema.default({}),
  lastUpdated: z.date().default(() => new Date()),
  version: z.string().default('1.0.0'),
});

// Infer types from schemas
export type QuickPayUsageItem = z.infer<typeof QuickPayUsageItemSchema>;
export type QuickPayUsageTracking = z.infer<typeof QuickPayUsageTrackingSchema>;
export type UIPreferences = z.infer<typeof UIPreferencesSchema>;
export type CheckoutPreferences = z.infer<typeof CheckoutPreferencesSchema>;
export type InventoryPreferences = z.infer<typeof InventoryPreferencesSchema>;
export type ReceiptPreferences = z.infer<typeof ReceiptPreferencesSchema>;
export type GeneralPreferences = z.infer<typeof GeneralPreferencesSchema>;
export type RestaurantPosPreferences = z.infer<typeof RestaurantPosPreferencesSchema>;
export type Preferences = z.infer<typeof PreferencesSchema>;

// Store type definition
export type PreferenceStoreType = {
  // State
  preferences: Preferences;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadPreferences: () => Promise<void>;
  updateUIPreferences: (preferences: Partial<UIPreferences>) => Promise<void>;
  updateCheckoutPreferences: (preferences: Partial<CheckoutPreferences>) => Promise<void>;
  updateInventoryPreferences: (preferences: Partial<InventoryPreferences>) => Promise<void>;
  updateReceiptPreferences: (preferences: Partial<ReceiptPreferences>) => Promise<void>;
  updateGeneralPreferences: (preferences: Partial<GeneralPreferences>) => Promise<void>;
  updateRestaurantPosPreferences: (
    preferences: Partial<RestaurantPosPreferences>
  ) => Promise<void>;
  updatePreferences: (preferences: Partial<Preferences>) => Promise<void>;
  resetPreferences: () => Promise<void>;
  resetCategory: (category: PreferenceCategory) => Promise<void>;

  // Quick Pay Usage Tracking
  recordQuickPayUsage: (cents: number) => Promise<void>;
  getTopQuickPayAmounts: (count?: number) => number[];
  clearQuickPayUsage: () => Promise<void>;
  getDefaultQuickPayAmounts: () => QuickPayUsageItem[];

  // Getters (computed values)
  getPreference: <T extends keyof Preferences>(
    category: T,
    key: keyof Preferences[T]
  ) => Preferences[T][keyof Preferences[T]];
};

// Default preferences with initial quick pay amounts
export const getDefaultPreferences = (): Preferences => {
  const defaultQuickPayAmounts: QuickPayUsageItem[] = [
    { cents: 500, usageCount: 1, lastUsed: new Date(), firstUsed: new Date() }, // $5
    { cents: 1000, usageCount: 1, lastUsed: new Date(), firstUsed: new Date() }, // $10
    { cents: 2000, usageCount: 1, lastUsed: new Date(), firstUsed: new Date() }, // $20
    { cents: 5000, usageCount: 1, lastUsed: new Date(), firstUsed: new Date() }, // $50
  ];

  return PreferencesSchema.parse({
    ui: {},
    checkout: {
      quickPayUsageTracking: {
        amounts: defaultQuickPayAmounts,
      },
    },
    inventory: {},
    receipts: {},
    general: {},
    restaurantPos: {},
  });
};
